import {
  Building2,
  CalendarDays,
  Eye,
  FileDown,
  Loader2,
  Lock,
  Mail,
  Receipt,
  RefreshCw,
  UserCheck,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import LockedCcChips from '@/components/common/LockedCcChips';
import { EmailBlockComposer } from '@/components/sales/wizard/EmailBlockComposer';
import { EmailSendPrereqNotice } from '@/components/sales/wizard/EmailSendPrereqNotice';
import { type StatusPatch } from '@/components/sales/wizard/StepLeadConfirmation';
import { EmailPreviewDialog, Kv, LinkChip, SummaryCard } from '@/components/sales/wizard/shared';
import { useToast } from '@/components/ui';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select-menu';
import { paymentModesFor } from '@/features/sales-rm/leadForm';
import {
  useEmailComposer,
  useEmailSendPrereqs,
  useFromSelector,
  useLeadEmailSent,
  useLeadPdfDownload,
  useLeadTcStatus,
  usePaymentInstallments,
  useSavePaymentInstallments,
} from '@/hooks';
import {
  inr,
  isDueMode,
  type LeadRecord,
  type PaymentMode,
  type PayStatus,
  type ProductInfo,
  type SetupQuoteResult,
  leadFullName,
} from '@/lib/sales-metrics';
import { buildWizardQuotationWithDiscount } from '@/lib/wizardQuotation';
import { type PaymentInstallmentRow } from '@/services/leads.service';
import { errorMessage } from '@/utils/apiError';

// Each summary section is a full-width card stacked one below the other (Fix 6);
// its cells flow horizontally and wrap to fill the width responsively.
const CARD_BODY = 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

// This step's template. A successful send (persisted server-side) unlocks
// "Client Onboarded" even after a refresh / reopen (Fix 3, reused for Step 4).
const BILLING_SLUGS = ['billing-notice-internal'];

// A row is "paid" once the RM fills in both a transaction number and a real
// payment mode. 'Due' is a mode but not a payment (ADR-053), so it fails here.
const isPaid = (row: PaymentInstallmentRow): boolean =>
  Boolean(row.transactionNo?.trim()) && Boolean(row.mode) && !isDueMode(row.mode);

// The client has not paid, but service starts anyway against the promised date
// in this row's own Due Date field. Billing receives it exactly like a paid row.
const isDue = (row: PaymentInstallmentRow): boolean => isDueMode(row.mode);

// Either way, the RM is finished with this row and it can be handed over.
const isCommitted = (row: PaymentInstallmentRow): boolean => isPaid(row) || isDue(row);

// Billing holds this row AND the money is in: that pair is the billing record,
// and the server rejects any change to it.
//
// Handed over but NOT paid is editable - a 'Due' row the RM is still chasing,
// or one being settled right now.
//
// Evaluated against the SERVER's copy of the schedule, never the live draft:
// see `lockedNumbers` in the component.
const isSettledWithBilling = (row: PaymentInstallmentRow): boolean =>
  Boolean(row.handedOverAt) && isPaid(row);

const payStatusBadgeClass = (status: PayStatus): string => {
  if (status === 'Full Payment') {
    return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
  }
  if (status === 'Partial Payment') {
    return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  }
  return 'bg-muted text-muted-foreground';
};

type StepBillingNoticeProps = Readonly<{
  lead: LeadRecord;
  selectedProducts: string[];
  catalog: ProductInfo[];
  setupQuote: SetupQuoteResult | null;
  rm: { id: number; name: string; email: string; employeeCode: string | null; rmId: string };
  isDone: boolean;
  isOnboarding: boolean;
  onOnboard: (payment: {
    paymentStatus: PayStatus;
    paymentAmount: number;
    paymentMode: PaymentMode | null;
  }) => void;
  onUpdateLead: (patch: StatusPatch) => void;
  customPrices: Record<string, number>;
  productTerms: Record<string, number>;
  renewalDiscountPercent: number;
  // Company bank chosen in Step 3 (ADR-020), displayed read-only per installment row.
  bankLabel: string;
}>;

export function StepBillingNotice({
  lead,
  selectedProducts,
  catalog,
  setupQuote,
  rm,
  isDone,
  isOnboarding,
  onOnboard,
  onUpdateLead,
  customPrices,
  productTerms,
  renewalDiscountPercent,
  bankLabel,
}: StepBillingNoticeProps) {
  const { toast } = useToast();

  const { items: quotationItems, totals } = buildWizardQuotationWithDiscount({
    catalog,
    selectedProducts,
    customPrices,
    productTerms,
    setupQuote,
    renewalDiscountPercent,
  });

  const { subtotal, total, discountLabel, gstAmount } = totals;
  const discountAmount = setupQuote?.discountAmount ?? 0;
  // Individual product names, rendered as tags in the Quotation card. Excludes
  // the synthetic aggregate "Discount" entry (a negative-priced line, not a
  // product) so the Products box lists only real products.
  const productList = quotationItems.filter((p) => p.price >= 0).map((p) => p.name);

  // ── Payment installment state ────────────────────────────────────────────
  // Loaded from the Payment Plan step; the RM fills in transactionNo + mode
  // here to mark individual rows as collected (green).
  const { data: installmentsData, isLoading: isLoadingInstallments } = usePaymentInstallments(
    lead.id,
    lead.orderId,
  );
  const saveInstallments = useSavePaymentInstallments();
  const [localRows, setLocalRows] = useState<PaymentInstallmentRow[]>([]);
  const [billingInitialized, setBillingInitialized] = useState(false);
  /**
   * Which installments were already settled with billing when this screen
   * opened. Captured once, from the server's copy.
   *
   * It cannot be recomputed from `localRows`: a row whose mode is set reads as
   * paid the instant the first character of the transaction number is typed, so
   * a live check would disable the field after one keystroke. Nor can it track
   * the query, because the debounced save refetches mid-typing and would lock
   * the field halfway through the number. A row settled during this session
   * locks when the wizard is next opened, which is when it truly is the
   * billing record.
   */
  const [lockedNumbers, setLockedNumbers] = useState<ReadonlySet<number>>(new Set());
  const skipBillingInitRef = useRef(true);
  // Lets the debounced save effect (declared before the composer) trigger a soft
  // preview refetch once the transaction number / mode is persisted, so the
  // billing email preview reflects the latest payment details as the RM types.
  const refetchPreviewRef = useRef<() => void>(() => {});

  // Initialize local rows once when the query resolves.
  useEffect(() => {
    if (billingInitialized || isLoadingInstallments) return;
    if (installmentsData !== undefined) {
      setLocalRows(installmentsData);
      setLockedNumbers(
        new Set(installmentsData.filter(isSettledWithBilling).map((r) => r.installmentNumber)),
      );
      setBillingInitialized(true);
    }
  }, [billingInitialized, isLoadingInstallments, installmentsData]);

  // A row is read-only for this session if billing already held it settled when
  // the screen opened.
  const isLocked = (row: PaymentInstallmentRow): boolean =>
    lockedNumbers.has(row.installmentNumber);

  // Derive payment status from which rows are fully filled in (green).
  const paidRows = localRows.filter(isPaid);
  const derivedStatus: PayStatus =
    paidRows.length === 0
      ? 'Not Paid'
      : paidRows.length === localRows.length
        ? 'Full Payment'
        : 'Partial Payment';
  const derivedAmount = paidRows.reduce((s, r) => s + r.amount, 0);
  // The order-level mode describes how the client PAID, so it comes from a paid
  // row. An order settled entirely on Due has none, and the answer is genuinely
  // NULL - not 'Due' (that is a promise, not a method) and not a default of
  // 'UPI', which would put a payment method on the record for money that never
  // arrived and show it back to the biller as fact.
  const derivedMode: PaymentMode | null =
    paidRows.length > 0 ? (paidRows[0].mode as PaymentMode) : (lead.paymentMode ?? null);

  // Debounced save of installments + lead payment-status sync whenever a row
  // changes after initialization (skip the very first set from the DB).
  useEffect(() => {
    if (skipBillingInitRef.current) {
      skipBillingInitRef.current = false;
      return undefined;
    }
    if (!billingInitialized || localRows.length === 0) return undefined;

    const pR = localRows.filter(isPaid);
    const status: PayStatus =
      pR.length === 0
        ? 'Not Paid'
        : pR.length === localRows.length
          ? 'Full Payment'
          : 'Partial Payment';
    const amount = pR.reduce((s, r) => s + r.amount, 0);

    const saveHandle = setTimeout(() => {
      saveInstallments.mutate(
        { id: lead.id, installments: localRows, orderId: lead.orderId ?? null },
        {
          // Refresh the billing email preview once the payment details persist, so
          // {{transaction_reference}} / {{installments_table}} update live (Fix 4).
          onSuccess: () => refetchPreviewRef.current(),
          // Surface a rejected save (e.g. a locked-row conflict) instead of
          // failing silently and leaving a green "paid" row the server won't honour.
          onError: (err) => toast.error(errorMessage(err, 'Could not save payment details')),
        },
      );
    }, 600);
    const statusHandle = setTimeout(() => {
      onUpdateLead({ paymentStatus: status, paymentAmount: amount });
    }, 600);

    return () => {
      clearTimeout(saveHandle);
      clearTimeout(statusHandle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localRows]);

  const updateTxnNo = (idx: number, value: string) => {
    setLocalRows((prev) =>
      isLocked(prev[idx])
        ? prev
        : prev.map((r, i) => (i === idx ? { ...r, transactionNo: value.slice(0, 100) } : r)),
    );
  };

  // Moves the promised payment date of a Due row. Only reachable while the row
  // is Due, where the date stands in for the transaction number.
  const updateDueDate = (idx: number, value: string) => {
    setLocalRows((prev) =>
      isLocked(prev[idx]) ? prev : prev.map((r, i) => (i === idx ? { ...r, dueDate: value } : r)),
    );
  };

  const updateMode = (idx: number, value: string) => {
    setLocalRows((prev) =>
      isLocked(prev[idx])
        ? prev
        : // Switching TO 'Due' clears any transaction number: the two are mutually
          // exclusive, and the server drops it anyway.
          prev.map((r, i) =>
            i === idx
              ? {
                  ...r,
                  mode: value || null,
                  transactionNo: isDueMode(value) ? null : r.transactionNo,
                }
              : r,
          ),
    );
  };

  // ── PDF / email / onboard ────────────────────────────────────────────────

  const { download: downloadPdf, downloading } = useLeadPdfDownload(lead.id);

  const prereqs = useEmailSendPrereqs();
  const { fromOptions, fromEmail, setFromChoice } = useFromSelector(rm.email);
  const { alreadySent: billingSent } = useLeadEmailSent(lead.id, BILLING_SLUGS);
  // Onboarding is gated on the lead having accepted the Terms & Conditions
  // (mentor's rule). Reaching Step 4 already implies acceptance, but this also
  // guards a lead reopened directly onto this step. The query shares its key with
  // Step 3, so it is served from cache with no extra request.
  const tcStatusQuery = useLeadTcStatus(lead.id);
  const tcConfirmed = (tcStatusQuery.data?.overallStatus ?? lead.tcStatus) === 'Confirmed';

  const templateSlug = 'billing-notice-internal';
  const composer = useEmailComposer({
    templateSlug,
    leadId: lead.id,
    enabled: quotationItems.length > 0,
    seedTo: true,
    fromEmail,
    defaults: {
      to: 'billing@advisorkhoj.com',
      cc: rm.email,
      bcc: '',
      subject: `AdvisorKhoj: Billing Handover - ${leadFullName(lead)} (${lead.arn})`,
    },
  });

  // Keep the ref pointing at the composer's soft refetch so the debounced save
  // effect (declared above the composer) can refresh the preview after a save.
  refetchPreviewRef.current = () => {
    void composer.refetchPreview();
  };

  // A transaction number is compulsory (Fix 5): an installment must carry BOTH a
  // transaction number and a mode, never one without the other. A row with only
  // one filled in is "half recorded" and blocks the handover/onboarding until the
  // RM either completes it or clears both.
  //
  // A 'Due' row is the exception: it deliberately carries no transaction number,
  // because the promised date is the record instead (ADR-053).
  const halfFilledRow = localRows.find(
    (r) => !isLocked(r) && !isDue(r) && Boolean(r.mode) !== Boolean(r.transactionNo?.trim()),
  );
  // At least one installment must be paid before a paid client is FIRST onboarded
  // (partial payment is allowed - the first paid installment is enough). An
  // already-onboarded client being edited (isDone) is never re-blocked, mirroring
  // the server's `firstOnboarding` scoping.
  //
  // Marking an installment Due satisfies this too: the client has not paid, but
  // service is starting and billing must be told, which is exactly what the gate
  // exists to guarantee.
  const requiresPayment = total > 0 && !isDone;
  const committedRows = localRows.filter(isCommitted);
  const hasAnyPaid = committedRows.length > 0;

  // Any paid installment (transaction + mode recorded) not yet handed over to
  // billing needs a FRESH send in this session before the Handover button can be
  // clicked - a historical send (from a prior session, before this payment was
  // recorded) doesn't cover it. When nothing is newly paid, a past send still
  // satisfies the gate (e.g. re-opening only to fix an unrelated detail).
  const hasUnhandledPaid = localRows.some((r) => isCommitted(r) && !r.handedOverAt);
  const canOnboard =
    tcConfirmed &&
    !halfFilledRow &&
    (!requiresPayment || hasAnyPaid) &&
    (hasUnhandledPaid ? composer.emailSent : composer.emailSent || billingSent);

  const sendEmail = () => {
    if (!prereqs.ready) {
      toast.error(prereqs.items[0].message);
      return;
    }
    if (quotationItems.length === 0) {
      toast.error('No products selected in Step 2');
      return;
    }
    if (halfFilledRow) {
      toast.error(
        `Installment ${halfFilledRow.installmentNumber}: enter both the transaction number and mode (or clear both).`,
      );
      return;
    }
    composer.send(
      (message) => toast.error(message),
      () => {
        toast.success(`Billing handover sent to ${composer.to}`);
        // Mirror the server's stamp (stampInstallmentsHandedOver runs during the
        // send) locally so paid rows lock immediately - without this, the table
        // only reflects "handed over" after a remount/refresh re-fetches the
        // installments query.
        // Mirrors what the server stamps: every COMMITTED row, which since
        // ADR-053 includes the Due ones. Mirroring only the paid ones left a Due
        // row reading as not-yet-handed-over until the page was remounted.
        setLocalRows((prev) =>
          prev.map((r) =>
            isCommitted(r) && !r.handedOverAt
              ? { ...r, handedOverAt: new Date().toISOString() }
              : r,
          ),
        );
      },
    );
  };

  const onboardClient = () => {
    if (!tcConfirmed) {
      toast.error('The lead must accept the Terms & Conditions before onboarding');
      return;
    }
    if (halfFilledRow) {
      toast.error(
        `Installment ${halfFilledRow.installmentNumber}: enter both the transaction number and mode (or clear both).`,
      );
      return;
    }
    if (requiresPayment && !hasAnyPaid) {
      toast.error('Record at least one payment (transaction number and mode) before onboarding.');
      return;
    }
    if (!canOnboard) {
      toast.error(
        hasUnhandledPaid
          ? 'Send the billing handover email for the new payment first'
          : 'Send the billing handover email first',
      );
      return;
    }
    void composer.refresh();
    onOnboard({
      paymentStatus: derivedStatus,
      paymentAmount: derivedAmount,
      paymentMode: derivedMode,
    });
  };

  const onboardButtonLabel = (): string => {
    if (isOnboarding) return 'Saving…';
    return isDone ? 'Handover' : 'Client Onboarded';
  };

  const arnHolderName = lead.form?.arnHolderName || leadFullName(lead);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold">Billing Handover</h3>
          <p className="text-xs text-muted-foreground">
            Send the billing team a handover with client info, quotation, and payment details. Then
            mark the lead as onboarded.
          </p>
        </div>
        <Button onClick={onboardClient} disabled={!canOnboard || isOnboarding}>
          <UserCheck className="h-4 w-4" />
          {onboardButtonLabel()}
        </Button>
      </div>

      <div className="flex flex-col gap-3">
        <SummaryCard
          icon={<UserCheck className="h-4 w-4" />}
          title="Sales RM"
          bodyClassName={CARD_BODY}
        >
          <Kv label="RM ID" v={String(rm.id)} />
          <Kv label="Name" v={rm.name} />
          <Kv label="Email" v={rm.email} />
          <Kv label="Employee Code" v={rm.employeeCode ?? '-'} />
          <Kv label="From Email" v={fromEmail || '-'} />
        </SummaryCard>

        <SummaryCard
          icon={<Building2 className="h-4 w-4" />}
          title="Client Info"
          bodyClassName={CARD_BODY}
        >
          <Kv label="Client ID" v={String(lead.leadCode)} />
          <Kv label="Name (ARN Holder)" v={arnHolderName} />
          <Kv label="Primary Email" v={lead.form?.primaryEmail || lead.email} />
          <Kv label="Primary Contact" v={lead.form?.primaryNumber || lead.mobile} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void downloadPdf('client');
            }}
            disabled={downloading !== null}
            className="mt-1 w-full"
          >
            {downloading === 'client' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="h-4 w-4" />
            )}{' '}
            Download Client PDF
          </Button>
        </SummaryCard>

        <SummaryCard
          icon={<Receipt className="h-4 w-4" />}
          title="Quotation & Products"
          bodyClassName="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
        >
          <div className="flex h-full flex-col rounded-md border border-border bg-muted/30 px-3 py-2 sm:col-span-2">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Products
            </span>
            {productList.length === 0 ? (
              <span className="mt-1 text-sm font-medium">-</span>
            ) : (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {productList.map((name) => (
                  <span
                    key={name}
                    className="inline-flex items-center rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-foreground"
                  >
                    {name}
                  </span>
                ))}
              </div>
            )}
          </div>
          <Kv label="Subtotal" v={inr(subtotal)} />
          <Kv label={discountAmount > 0 ? discountLabel : 'Discount'} v={inr(discountAmount)} />
          <Kv label={`GST ${setupQuote?.gstRate ?? 18}%`} v={inr(gstAmount)} />
          <Kv label="Total Payable" v={inr(total)} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void downloadPdf('quotation');
            }}
            disabled={quotationItems.length === 0 || downloading !== null}
            className="mt-1 w-full sm:col-span-2"
          >
            {downloading === 'quotation' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="h-4 w-4" />
            )}{' '}
            Download Quotation PDF
          </Button>
        </SummaryCard>

        {/* Payment Details - editable installment table. Status auto-derived from
            green (fully-filled) rows; the RM fills Transaction No. and Mode per
            row once payment is collected. Amount and Due Date are read-only here
            (set in the Payment Plan step). Bank is read-only (from Step 3). */}
        {localRows.length > 0 && (
          <div className="flex flex-col gap-2 rounded-lg border border-border bg-background p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <CalendarDays className="h-4 w-4" /> Payment Details
              </h4>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${payStatusBadgeClass(derivedStatus)}`}
              >
                {derivedStatus}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="w-8 pb-2 pr-4 text-left font-medium">#</th>
                    <th className="pb-2 pr-4 text-left font-medium">Amount</th>
                    <th className="pb-2 pr-4 text-left font-medium">Due Date</th>
                    <th className="pb-2 pr-4 text-left font-medium">Bank</th>
                    <th className="pb-2 pr-4 text-left font-medium">Mode</th>
                    <th className="pb-2 pr-4 text-left font-medium">Transaction No. / Due On</th>
                    <th className="pb-2 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {localRows.map((row, idx) => {
                    const locked = isLocked(row);
                    const due = isDue(row);
                    return (
                      <tr
                        key={row.installmentNumber}
                        className={`transition-colors ${(() => {
                          if (isPaid(row)) return 'bg-emerald-50 dark:bg-emerald-900/20';
                          return due ? 'bg-amber-50 dark:bg-amber-900/20' : '';
                        })()}`}
                      >
                        <td className="py-2.5 pr-4">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                            {idx + 1}
                          </span>
                        </td>
                        <td className="py-2.5 pr-4 font-medium text-foreground">
                          {inr(row.amount)}
                        </td>
                        <td className="py-2.5 pr-4 text-muted-foreground">{row.dueDate}</td>
                        <td className="whitespace-nowrap py-2.5 pr-4 text-muted-foreground">
                          {bankLabel || '-'}
                        </td>
                        <td className="py-2.5 pr-4">
                          <Select
                            value={row.mode ?? ''}
                            onChange={(e) => updateMode(idx, e.target.value)}
                            disabled={locked}
                            className="h-8 w-36"
                            aria-label={`Installment ${idx + 1} payment mode`}
                          >
                            <option value="">- Select -</option>
                            {/* 'Due' is offered on the first installment only:
                                it starts the client's service before payment,
                                and service starts once (ADR-053). */}
                            {paymentModesFor(row.installmentNumber).map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </Select>
                        </td>
                        <td className="py-2.5 pr-4">
                          {/* 'Due' replaces the transaction number with the date
                              the client has promised to pay by - there is no
                              transaction yet, so the date is the record. */}
                          {due ? (
                            <Input
                              type="date"
                              value={row.dueDate}
                              onChange={(e) => updateDueDate(idx, e.target.value)}
                              disabled={locked}
                              className="h-8 w-36"
                              aria-label={`Installment ${idx + 1} promised payment date`}
                            />
                          ) : (
                            <Input
                              value={row.transactionNo ?? ''}
                              maxLength={100}
                              placeholder="e.g. TXN123456"
                              onChange={(e) => updateTxnNo(idx, e.target.value)}
                              disabled={locked}
                              className="h-8 w-36"
                              aria-label={`Installment ${idx + 1} transaction number`}
                            />
                          )}
                        </td>
                        <td className="whitespace-nowrap py-2.5">
                          {(() => {
                            if (locked) {
                              return (
                                <span
                                  className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400"
                                  title="Handed over to billing"
                                >
                                  <Lock className="h-3 w-3" />
                                  Handed over
                                </span>
                              );
                            }
                            if (due) {
                              return (
                                <span
                                  className="text-xs font-medium text-amber-700 dark:text-amber-400"
                                  title="Service starts now; the client pays by the date shown"
                                >
                                  {row.handedOverAt ? 'Due (with billing)' : 'Due'}
                                </span>
                              );
                            }
                            // Was Due, now being settled: billing holds it but
                            // the transaction number is still missing.
                            if (row.handedOverAt && !isPaid(row)) {
                              return (
                                <span
                                  className="text-xs font-medium text-amber-700 dark:text-amber-400"
                                  title="Enter the transaction number to record this payment"
                                >
                                  Awaiting transaction no.
                                </span>
                              );
                            }
                            if (isPaid(row)) {
                              return <span className="text-xs text-muted-foreground">Paid</span>;
                            }
                            // Untouched: nothing recorded against it, so it is
                            // simply not due yet. Deliberately NOT defaulted to
                            // 'Due' - that would hand it to billing today for
                            // money nobody expects yet (ADR-053).
                            return (
                              <span
                                className="text-xs text-muted-foreground"
                                title="Not due yet. It goes to billing when you record the payment, or when you mark it Due to start service against a promised date."
                              >
                                Scheduled
                              </span>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {(localRows.length > 1 || paidRows.length > 0) && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                {localRows.length > 1 && (
                  <>
                    Total scheduled:{' '}
                    <span className="font-semibold text-foreground">
                      {inr(localRows.reduce((s, r) => s + r.amount, 0))}
                    </span>
                  </>
                )}
                {paidRows.length > 0 && localRows.length > 1 && ' · '}
                {paidRows.length > 0 && (
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                    {inr(derivedAmount)} collected
                  </span>
                )}
              </p>
            )}

            {/* Says that leaving the later installments blank is the correct
                thing to do, not an omission. Without it an RM tends to fill
                something in, and the only thing they can fill in is 'Due',
                which would push money that is not owed yet into billing. */}
            {localRows.some((r) => !isCommitted(r)) && (
              <p className="mt-1 text-xs text-muted-foreground">
                Leave an installment blank until it is actually collected - it stays out of billing
                and comes to them when you record the payment. Mark one{' '}
                <span className="font-semibold text-foreground">Due</span> only when service should
                start before the client has paid.
              </p>
            )}

            {/* Settling a Due installment sends it back to billing at step 1,
                because everything the biller verified described a promise
                rather than a receipt. Said here so it is not a surprise. */}
            {localRows.some(isDue) && (
              <p className="mt-1 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
                A &ldquo;Due&rdquo; installment goes to billing on the client&rsquo;s promise to pay
                by the date shown, and service starts. When the payment actually arrives, change the
                mode and enter the transaction number: billing then re-verifies that installment
                from the first step against the real payment, unless they had already finished
                invoicing it.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-border bg-background p-4">
        <div className="flex items-center justify-between gap-2">
          <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Mail className="h-3.5 w-3.5" /> Email to Billing Team
          </h4>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            {fromOptions.length > 0 && (
              <div className="flex w-full min-w-0 items-center gap-1.5 sm:w-auto">
                <Label className="text-xs text-muted-foreground">From</Label>
                <Select
                  value={fromEmail}
                  onChange={(e) => setFromChoice(e.target.value)}
                  className="h-9 w-full min-w-0 sm:w-72"
                  aria-label="Sender (From) address for this email"
                >
                  {fromOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void composer.refresh();
              }}
              disabled={composer.isPreviewFetching}
              title="Refresh email contents from the latest data"
            >
              <RefreshCw
                className={`h-4 w-4 ${composer.isPreviewFetching ? 'animate-spin' : ''}`}
              />{' '}
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={() => composer.setOpenPreview(true)}>
              <Eye className="h-4 w-4" /> Preview
            </Button>
          </div>
        </div>
        {composer.isPreviewLoading && (
          <p className="text-[11px] text-muted-foreground">
            Loading the rendered template (bank details, reference number)…
          </p>
        )}
        {composer.isPreviewError && (
          <p className="text-[11px] text-danger">
            Could not load the email template - check that &quot;{templateSlug}&quot; exists and is
            active.
          </p>
        )}
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="grid gap-1.5 sm:col-span-2">
            <Label className="text-xs">To</Label>
            <Input
              value={composer.to}
              onChange={(e) => composer.setTo(e.target.value)}
              placeholder="name@example.com, second@example.com"
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">CC</Label>
            <Input
              value={composer.cc}
              onChange={(e) => composer.setCc(e.target.value)}
              placeholder="comma-separated for multiple"
            />
            <LockedCcChips category="internal" />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">BCC</Label>
            <Input
              value={composer.bcc}
              onChange={(e) => composer.setBcc(e.target.value)}
              placeholder="optional, comma-separated"
            />
          </div>
          <div className="grid gap-1.5 sm:col-span-2">
            <Label className="text-xs">Subject</Label>
            <Input value={composer.subject} onChange={(e) => composer.setSubject(e.target.value)} />
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Body</Label>
          <EmailBlockComposer
            items={composer.items}
            dragIndex={composer.dragIndex}
            onDragStart={composer.handleDragStart}
            onDragEnd={composer.handleDragEnd}
            onDragOverIndex={composer.handleDragOverIndex}
            onUpdateRmBlock={composer.updateRmBlock}
            onRemoveItem={composer.removeItem}
            onAddBlock={composer.addBlock}
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <LinkChip>Client Info PDF (attached)</LinkChip>
          <LinkChip>Quotation PDF (attached)</LinkChip>
        </div>
        <EmailSendPrereqNotice items={prereqs.items} />
        <div className="flex items-center justify-between pt-1">
          {!canOnboard ? (
            <p className="text-[11px] text-muted-foreground">
              {!tcConfirmed
                ? 'The lead must accept the Terms & Conditions before you can onboard.'
                : halfFilledRow
                  ? `Complete installment ${halfFilledRow.installmentNumber}: enter both the transaction number and mode (or clear both).`
                  : requiresPayment && !hasAnyPaid
                    ? 'Record at least one payment (transaction number and mode) to onboard.'
                    : hasUnhandledPaid
                      ? 'Send the billing handover for the new payment to enable Handover.'
                      : `Send the billing handover to enable "${isDone ? 'Handover' : 'Client Onboarded'}".`}
            </p>
          ) : (
            <p className="text-[11px] text-emerald-600">
              Billing handover sent. You can now{' '}
              {isDone ? 'hand over the client' : 'mark the client as onboarded'}.
            </p>
          )}
          <Button
            onClick={sendEmail}
            disabled={
              !prereqs.ready ||
              quotationItems.length === 0 ||
              !composer.canSend ||
              composer.isSending ||
              composer.isPreviewFetching
            }
          >
            <Mail className="h-4 w-4" />
            {composer.sendEmailButtonLabel()}
          </Button>
        </div>
      </div>

      <EmailPreviewDialog
        open={composer.openPreview}
        onOpenChange={composer.setOpenPreview}
        subject={composer.subject}
        bodyHtml={composer.bodyHtml}
      />
    </div>
  );
}
